import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import {
  TrendingUp, TrendingDown, Users, Package, DollarSign, Activity, Sparkles,
  ArrowRight, X, Search, GitCompare, ChevronDown, ChevronRight, Receipt,
  Wallet, Calendar, Award, Repeat, Clock, AlertTriangle, Brain, Truck,
} from 'lucide-react';
import { MonthlyCashflowTable } from '../components/MonthlyCashflowTable';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);
const fmtN = (n) => new Intl.NumberFormat('en-IN', { maximumFractionDigits: 1 }).format(n || 0);

/**
 * Insights v3 — multi-compare + drill-down + richer client/business data.
 *
 *  • Single-pick mode: deep-dive on one client or one product.
 *  • Compare mode: pick 2-3 clients OR 2-3 products → side-by-side metrics.
 *  • Drill-down: click any month row in the cross-tab → expand to show
 *    individual invoices in that month with bill# / date / total / items.
 *  • Macro view: KPI tiles, monthly chart, top clients/products, churn,
 *    mix-and-match pivot — same as iter157.
 */
export default function Insights() {
  const { user } = useAuth();
  const canSeePrices = user?.role === 'owner' || user?.role === 'admin' || user?.permissions?.can_see_prices;
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiInsights, setAiInsights] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);

  const [xAxis, setXAxis] = useState('month');
  const [yAxis, setYAxis] = useState('revenue');
  const [mixData, setMixData] = useState(null);
  const [mixLoading, setMixLoading] = useState(false);

  // Picker state — supports single or multi (compare mode).
  const [compareMode, setCompareMode] = useState(false);
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [clientSearch, setClientSearch] = useState('');
  const [productSearch, setProductSearch] = useState('');
  const [pickedClients, setPickedClients] = useState([]);   // array of client objects
  const [cashflowClient, setCashflowClient] = useState('');
  const [pickedProducts, setPickedProducts] = useState([]); // array of product objects
  // Raw-material lens (single-select deep dive, mutually exclusive with client/product).
  const [rawMaterials, setRawMaterials] = useState([]);
  const [rmSearch, setRmSearch] = useState('');
  const [pickedRM, setPickedRM] = useState([]);
  const [rmDetail, setRmDetail] = useState(null);
  const [rmLoading, setRmLoading] = useState(false);

  // Deep-dive state (single mode).
  const [clientDetail, setClientDetail] = useState(null);
  const [productDetail, setProductDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [microInsights, setMicroInsights] = useState([]);
  const [microLoading, setMicroLoading] = useState(false);

  // Compare data.
  const [compareData, setCompareData] = useState(null);
  const [compareLoading, setCompareLoading] = useState(false);

  // Drill-down state — { month: 'YYYY-MM', invoices: [...] } or null.
  const [expandedMonth, setExpandedMonth] = useState(null);
  const [monthInvoices, setMonthInvoices] = useState({});

  // ── Bootstrap ────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const [s, c, p, rm] = await Promise.all([
          axios.get(`${API}/insights/summary?months=6`, { withCredentials: true }),
          axios.get(`${API}/clients/`, { withCredentials: true }),
          axios.get(`${API}/inventory/finished-goods`, { withCredentials: true }),
          axios.get(`${API}/insights/raw-materials`, { withCredentials: true }),
        ]);
        setSummary(s.data);
        setClients(c.data || []);
        setProducts(p.data || []);
        setRawMaterials(rm.data?.materials || []);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, []);

  // ── Mix-match pivot ──────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      setMixLoading(true);
      try {
        const r = await axios.get(`${API}/insights/mix-match?x_axis=${xAxis}&y_axis=${yAxis}`, { withCredentials: true });
        setMixData(r.data);
      } catch (e) { console.error(e); }
      finally { setMixLoading(false); }
    })();
  }, [xAxis, yAxis]);

  // ── Single-mode deep-dive loaders ────────────────────────────────
  const singleClient = !compareMode && pickedClients[0];
  const singleProduct = !compareMode && pickedProducts[0];

  useEffect(() => {
    if (!singleClient) { setClientDetail(null); return; }
    (async () => {
      setDetailLoading(true); setMicroInsights([]); setExpandedMonth(null);
      try {
        const r = await axios.get(`${API}/insights/client/${singleClient.id}`, { withCredentials: true });
        setClientDetail(r.data);
      } catch (e) { console.error(e); }
      finally { setDetailLoading(false); }
    })();
  }, [singleClient?.id]);

  useEffect(() => {
    if (!singleProduct) { setProductDetail(null); return; }
    (async () => {
      setDetailLoading(true); setMicroInsights([]);
      try {
        const r = await axios.get(`${API}/insights/product/${singleProduct.id}`, { withCredentials: true });
        setProductDetail(r.data);
      } catch (e) { console.error(e); }
      finally { setDetailLoading(false); }
    })();
  }, [singleProduct?.id]);

  // ── Compare mode loader ──────────────────────────────────────────
  useEffect(() => {
    if (!compareMode) { setCompareData(null); return; }
    const entity = pickedClients.length >= 2 ? 'client' : pickedProducts.length >= 2 ? 'product' : null;
    const ids = entity === 'client' ? pickedClients.map(x => x.id) : entity === 'product' ? pickedProducts.map(x => x.id) : [];
    if (!entity || ids.length < 2) { setCompareData(null); return; }
    (async () => {
      setCompareLoading(true);
      try {
        const r = await axios.post(`${API}/insights/compare`, { entity_type: entity, ids }, { withCredentials: true });
        setCompareData(r.data);
      } catch (e) { console.error(e); setCompareData(null); }
      finally { setCompareLoading(false); }
    })();
  }, [compareMode, JSON.stringify(pickedClients.map(c => c.id)), JSON.stringify(pickedProducts.map(p => p.id))]);

  // ── AI summaries ─────────────────────────────────────────────────
  const runAiSummary = async () => {
    if (!summary) return;
    setAiLoading(true); setAiInsights([]);
    try {
      const context = JSON.stringify({
        revenue: summary.total_revenue, cost: summary.total_cost, margin_pct: summary.margin_pct,
        top_clients: summary.top_clients.slice(0, 5), top_products: summary.top_products.slice(0, 5),
        monthly: summary.monthly, churning: summary.churning_clients,
      });
      const r = await axios.post(`${API}/insights/ai-summary`, { context }, { withCredentials: true });
      setAiInsights(r.data.insights || []);
    } catch (e) { setAiInsights(['AI summary failed — try again.']); }
    finally { setAiLoading(false); }
  };

  const runMicroInsight = async () => {
    const target = clientDetail || productDetail;
    if (!target) return;
    setMicroLoading(true);
    try {
      const context = JSON.stringify(clientDetail ? {
        client: clientDetail.client.name,
        total_orders: clientDetail.total_orders,
        total_revenue: clientDetail.total_revenue,
        outstanding: clientDetail.outstanding,
        avg_order_value: clientDetail.avg_order_value,
        order_frequency_days: clientDetail.order_frequency_days,
        best_month: clientDetail.best_month,
        best_product: clientDetail.best_product,
        payment_score: clientDetail.payment_score,
        product_mix: clientDetail.product_mix.slice(0, 5),
        monthly_revenue: clientDetail.monthly_revenue,
        order_timing: clientDetail.order_timing,
        predictions: clientDetail.predictions?.slice(0, 5),
        procurement_short: clientDetail.procurement_forecast?.filter(m => m.action === 'REORDER').slice(0, 5).map(m => ({ material: m.material, shortage: m.shortage, unit: m.unit })),
      } : {
        product: productDetail.product.name,
        total_revenue: productDetail.total_revenue,
        total_qty: productDetail.total_qty,
        avg_price: productDetail.avg_price,
        min_price: productDetail.min_price,
        max_price: productDetail.max_price,
        std_price: productDetail.std_price,
        avg_cost: productDetail.avg_cost,
        margin_pct_est: productDetail.margin_pct_est,
        top_clients: productDetail.client_mix.slice(0, 5),
      });
      const r = await axios.post(`${API}/insights/ai-summary`, { context }, { withCredentials: true });
      setMicroInsights(r.data.insights || []);
    } catch (e) { setMicroInsights(['AI insight failed — try again.']); }
    finally { setMicroLoading(false); }
  };

  // ── Drill-down: expand a month row to show its invoices ──────────
  const toggleMonth = async (month) => {
    if (!singleClient) return;
    if (expandedMonth === month) { setExpandedMonth(null); return; }
    setExpandedMonth(month);
    if (!monthInvoices[month]) {
      try {
        const r = await axios.get(`${API}/insights/client/${singleClient.id}/invoices?month=${month}`, { withCredentials: true });
        setMonthInvoices(prev => ({ ...prev, [month]: r.data.invoices }));
      } catch (e) { console.error(e); setMonthInvoices(prev => ({ ...prev, [month]: [] })); }
    }
  };

  // ── iter161: dedicated Client × Product fetch (substring matching) ──
  const [cpData, setCpData] = useState(null);
  const [cpLoading, setCpLoading] = useState(false);
  useEffect(() => {
    if (!singleClient || !singleProduct) { setCpData(null); return; }
    let cancelled = false;
    (async () => {
      setCpLoading(true);
      try {
        const r = await axios.get(
          `${API}/insights/client/${singleClient.id}/product/${singleProduct.id}/monthly`,
          { withCredentials: true },
        );
        if (!cancelled) setCpData(r.data);
      } catch (e) {
        console.error(e);
        if (!cancelled) setCpData(null);
      } finally {
        if (!cancelled) setCpLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [singleClient?.id, singleProduct?.id]);

  // ── Picker actions (single vs compare) ───────────────────────────
  // In single mode, client + product can both be picked simultaneously so
  // the focused "Client × Product" view shows up. In compare mode they
  // remain mutually exclusive (pick 2-3 of ONE type).
  const pickClient = (c) => {
    setPickedRM([]);
    if (compareMode) {
      setPickedProducts([]);  // compare mode is one-axis only
      setPickedClients(prev => prev.find(x => x.id === c.id) ? prev : [...prev, c].slice(0, 3));
    } else {
      setPickedClients([c]);
    }
  };
  const pickProduct = (p) => {
    setPickedRM([]);
    if (compareMode) {
      setPickedClients([]);
      setPickedProducts(prev => prev.find(x => x.id === p.id) ? prev : [...prev, p].slice(0, 3));
    } else {
      setPickedProducts([p]);
    }
  };
  const pickRM = (m) => { setPickedClients([]); setPickedProducts([]); setPickedRM([m]); };
  const removeRM = (id) => setPickedRM(prev => prev.filter(m => m.id !== id));
  const removeClient = (id) => setPickedClients(prev => prev.filter(c => c.id !== id));
  const removeProduct = (id) => setPickedProducts(prev => prev.filter(p => p.id !== id));
  const clearAll = () => { setPickedClients([]); setPickedProducts([]); setPickedRM([]); setMicroInsights([]); setExpandedMonth(null); };

  // ── Raw-material deep-dive loader ────────────────────────────────
  const singleRM = !compareMode && pickedRM[0];
  useEffect(() => {
    if (!singleRM) { setRmDetail(null); return; }
    (async () => {
      setRmLoading(true);
      try {
        const r = await axios.get(`${API}/insights/raw-material/${singleRM.id}`, { withCredentials: true });
        setRmDetail(r.data);
      } catch (e) { console.error(e); setRmDetail(null); }
      finally { setRmLoading(false); }
    })();
  }, [singleRM?.id]);

  // ── Derived ──────────────────────────────────────────────────────
  const maxMonthly = useMemo(() => Math.max(1, ...(summary?.monthly || []).map(m => Math.max(m.revenue, m.cost))), [summary]);
  const maxMix = useMemo(() => Math.max(1, ...(mixData?.rows || []).map(r => r.value)), [mixData]);
  const filteredClients = useMemo(() => {
    const q = clientSearch.trim().toLowerCase();
    return clients.filter(c => !q || c.name?.toLowerCase().includes(q)).slice(0, 8);
  }, [clients, clientSearch]);
  const filteredProducts = useMemo(() => {
    const q = productSearch.trim().toLowerCase();
    return products.filter(p => !q || p.name?.toLowerCase().includes(q)).slice(0, 8);
  }, [products, productSearch]);
  const filteredRM = useMemo(() => {
    const q = rmSearch.trim().toLowerCase();
    return rawMaterials.filter(m => !q || m.name?.toLowerCase().includes(q)).slice(0, 8);
  }, [rawMaterials, rmSearch]);

  if (loading) return <div className="p-8 text-zinc-400">Loading insights…</div>;
  if (!summary) return <div className="p-8 text-zinc-400">No data available.</div>;

  return (
    <div className="p-6 space-y-6" data-testid="insights-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-zinc-100">Business Insights</h1>
          <p className="text-zinc-500 text-sm mt-1">
            {compareMode
              ? 'Compare mode — pick 2–3 clients OR 2–3 products to view side-by-side.'
              : 'Pick a client or product to drill down — or scan the macro view below.'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => { setCompareMode(v => !v); clearAll(); }}
            data-testid="toggle-compare-mode"
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs border transition-colors ${
              compareMode
                ? 'bg-orange-500/20 text-orange-300 border-orange-500/40'
                : 'bg-zinc-800 text-zinc-300 border-zinc-700 hover:bg-zinc-700'
            }`}>
            <GitCompare size={13}/> {compareMode ? 'Compare Mode ON' : 'Compare Mode'}
          </button>
          <button onClick={runAiSummary} disabled={aiLoading} data-testid="run-ai-summary"
            className="flex items-center gap-2 bg-fuchsia-500/15 hover:bg-fuchsia-500/25 text-fuchsia-300 border border-fuchsia-500/30 px-3 py-2 rounded-lg text-xs">
            <Sparkles size={13}/> {aiLoading ? 'Analyzing…' : 'AI Macro Summary'}
          </button>
        </div>
      </div>

      {/* ── Pickers ─────────────────────────────────────────────── */}
      <div className={`grid grid-cols-1 gap-4 ${compareMode ? 'lg:grid-cols-2' : 'lg:grid-cols-3'}`}>
        <PickerCard
          label="Client / Debtor"
          accent="emerald"
          icon={Users}
          search={clientSearch}
          onSearch={setClientSearch}
          items={filteredClients}
          picked={pickedClients}
          onPick={pickClient}
          onRemove={removeClient}
          testid="client-picker"
          multi={compareMode}
        />
        <PickerCard
          label="Product"
          accent="sky"
          icon={Package}
          search={productSearch}
          onSearch={setProductSearch}
          items={filteredProducts}
          picked={pickedProducts}
          onPick={pickProduct}
          onRemove={removeProduct}
          testid="product-picker"
          multi={compareMode}
        />
        {!compareMode && (
          <PickerCard
            label="Raw Material"
            accent="amber"
            icon={Package}
            search={rmSearch}
            onSearch={setRmSearch}
            items={filteredRM}
            picked={pickedRM}
            onPick={pickRM}
            onRemove={removeRM}
            testid="rm-picker"
            multi={false}
          />
        )}
      </div>

      {/* ── Compare panel ───────────────────────────────────────── */}
      {compareMode && compareData && compareData.columns.length >= 2 && (
        <CompareTable data={compareData} loading={compareLoading} />
      )}
      {compareMode && !compareData && (pickedClients.length + pickedProducts.length) < 2 && (
        <div className="bg-zinc-900 border border-zinc-800 border-dashed rounded-xl p-6 text-center text-zinc-500 text-xs" data-testid="compare-hint">
          Pick at least 2 entities (up to 3) from the same picker above to see them side-by-side.
        </div>
      )}

      {/* ── Combined Client × Product focus (when BOTH picked in single mode) ── */}
      {!compareMode && singleClient && singleProduct && (
        <ClientProductFocus
          clientName={singleClient.name}
          productName={singleProduct.name}
          data={cpData}
          loading={cpLoading}
        />
      )}

      {/* ── Single-mode deep dive ───────────────────────────────── */}
      {!compareMode && (singleClient || singleProduct) && (
        <div className="bg-zinc-900 border-2 border-amber-500/30 rounded-xl p-5 space-y-5" data-testid="deep-dive-panel">
          {detailLoading ? (
            <div className="text-zinc-500 text-sm">Loading deep dive…</div>
          ) : clientDetail ? (
            <ClientDeepDive
              detail={clientDetail}
              microInsights={microInsights}
              microLoading={microLoading}
              onMicro={runMicroInsight}
              expandedMonth={expandedMonth}
              monthInvoices={monthInvoices}
              onToggleMonth={toggleMonth}
            />
          ) : productDetail ? (
            <ProductDeepDive
              detail={productDetail}
              microInsights={microInsights}
              microLoading={microLoading}
              onMicro={runMicroInsight}
            />
          ) : null}
        </div>
      )}

      {/* ── Raw-material deep dive ──────────────────────────────── */}
      {!compareMode && singleRM && (
        <div className="bg-zinc-900 border-2 border-amber-500/30 rounded-xl p-5 space-y-5" data-testid="rm-deep-dive-panel">
          {rmLoading ? (
            <div className="text-zinc-500 text-sm">Loading raw-material insight…</div>
          ) : rmDetail ? (
            <RawMaterialDeepDive detail={rmDetail} canSeePrices={canSeePrices} />
          ) : (
            <div className="text-zinc-500 text-sm">No data for this material.</div>
          )}
        </div>
      )}

      {/* ── Customer monthly cash flow ──────────────────────────── */}
      <div className="bg-zinc-900/60 border border-zinc-800 rounded-xl" data-testid="insights-cashflow-section">
        <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-zinc-800">
          <div>
            <div className="text-emerald-300 text-xs uppercase tracking-wider font-bold flex items-center gap-2"><Users size={12}/> Customer cash flow · month-wise</div>
            <div className="text-zinc-500 text-[11px] mt-0.5">Sales, product quantities, what fell due, cash received and whether they paid as per due.</div>
          </div>
          <select value={cashflowClient} onChange={e => setCashflowClient(e.target.value)} data-testid="cf-client-select"
            className="bg-zinc-950 border border-zinc-700 rounded-lg px-2 py-1.5 text-zinc-200 text-xs max-w-[260px]">
            <option value="">All customers</option>
            {clients.filter(c => c.type !== 'Creditor').map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <MonthlyCashflowTable clientId={cashflowClient || null} />
      </div>

      {/* ── Macro AI summary ────────────────────────────────────── */}
      {aiInsights.length > 0 && (
        <div className="bg-fuchsia-500/10 border border-fuchsia-500/30 rounded-xl p-4" data-testid="ai-summary-panel">
          <div className="text-fuchsia-300 text-xs uppercase tracking-wider mb-2 font-bold flex items-center gap-2"><Sparkles size={12}/> AI Macro Insights</div>
          <ul className="space-y-1.5 text-zinc-200 text-sm">
            {aiInsights.map((ins, i) => <li key={i} className="flex gap-2"><ArrowRight size={14} className="text-fuchsia-400 flex-shrink-0 mt-0.5"/><span>{ins}</span></li>)}
          </ul>
        </div>
      )}

      {/* 4 KPI tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Revenue',  val: fmt(summary.total_revenue), tone: 'emerald', icon: DollarSign },
          { label: 'Cost',     val: fmt(summary.total_cost),    tone: 'rose',    icon: Package },
          { label: 'Margin',   val: fmt(summary.total_margin),  tone: 'amber',   icon: TrendingUp },
          { label: 'Margin %', val: `${summary.margin_pct}%`,   tone: 'sky',     icon: Activity },
        ].map(k => (
          <div key={k.label} className="bg-zinc-900 border border-zinc-800 rounded-xl p-4" data-testid={`kpi-${k.label.toLowerCase().replace(' ','-')}`}>
            <div className="flex items-center justify-between">
              <span className="text-zinc-500 text-xs uppercase tracking-wider">{k.label}</span>
              <k.icon size={14} className={`text-${k.tone}-400`}/>
            </div>
            <div className={`mt-2 text-2xl font-bold text-${k.tone}-300`}>{k.val}</div>
          </div>
        ))}
      </div>

      {/* Monthly bar chart */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
        <div className="text-zinc-300 text-sm font-semibold mb-3">Monthly Revenue vs Cost</div>
        <div className="flex gap-3 h-44">
          {summary.monthly.map(m => {
            const rh = (m.revenue / maxMonthly) * 100;
            const ch = (m.cost / maxMonthly) * 100;
            return (
              <div key={m.month} className="flex-1 flex flex-col items-center gap-1 h-full">
                <div className="w-full flex items-end justify-center gap-1 flex-1 min-h-0">
                  <div className="w-2/5 bg-emerald-500 rounded-t" style={{ height: `${Math.max(rh, 1)}%` }} title={`Revenue ${fmt(m.revenue)}`}/>
                  <div className="w-2/5 bg-rose-500/70 rounded-t" style={{ height: `${Math.max(ch, 1)}%` }} title={`Cost ${fmt(m.cost)}`}/>
                </div>
                <div className="text-[10px] text-zinc-500 font-mono">{m.month.slice(5)}</div>
              </div>
            );
          })}
        </div>
        <div className="flex items-center gap-3 mt-3 text-[10px] text-zinc-500">
          <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 bg-emerald-500 rounded"/>Revenue</span>
          <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 bg-rose-500/70 rounded"/>Cost</span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <TopList icon={Users} title="Top Clients" tone="emerald" testid="top-client" items={summary.top_clients.slice(0, 8)} k1="client" k2="orders" k2Label="orders" k3="revenue"
          onItemClick={(c) => { const match = clients.find(x => x.name === c.client); if (match) pickClient(match); }} />
        <TopList icon={Package} title="Top Products" tone="sky" testid="top-product" items={summary.top_products.slice(0, 8)} k1="product" k2="qty" k2Label="qty" k3="revenue"
          onItemClick={(p) => { const match = products.find(x => x.name === p.product); if (match) pickProduct(match); }} />
      </div>

      {summary.churning_clients.length > 0 && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4" data-testid="churning-panel">
          <div className="flex items-center gap-2 mb-2"><TrendingDown size={14} className="text-amber-400"/><span className="text-amber-300 text-sm font-semibold">Clients to Re-engage</span></div>
          <div className="space-y-1">
            {summary.churning_clients.map(c => (
              <div key={c.client} className="flex items-center justify-between text-xs">
                <span className="text-zinc-200">{c.client}</span>
                <span className="text-zinc-500">{c.days_silent}d silent · last {c.last_order?.slice(0, 10)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
        <div className="text-zinc-300 text-sm font-semibold mb-3">Mix & Match — Pivot anything</div>
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <label className="text-zinc-500 text-[11px]">X axis:</label>
          <select value={xAxis} onChange={e => setXAxis(e.target.value)} data-testid="mix-x-axis"
            className="bg-zinc-800 border border-zinc-700 rounded-lg px-2 py-1 text-zinc-200 text-xs focus:outline-none focus:border-amber-500">
            {['month', 'client', 'product', 'vendor'].map(o => <option key={o} value={o}>{o}</option>)}
          </select>
          <label className="text-zinc-500 text-[11px]">Y metric:</label>
          <select value={yAxis} onChange={e => setYAxis(e.target.value)} data-testid="mix-y-axis"
            className="bg-zinc-800 border border-zinc-700 rounded-lg px-2 py-1 text-zinc-200 text-xs focus:outline-none focus:border-amber-500">
            {['revenue', 'qty', 'orders', 'margin', 'cost'].map(o => <option key={o} value={o}>{o}</option>)}
          </select>
          {mixLoading && <span className="text-zinc-500 text-[10px]">loading…</span>}
        </div>
        {mixData?.rows && mixData.rows.length > 0 ? (
          <div className="space-y-1.5">
            {mixData.rows.slice(0, 15).map(r => (
              <div key={r.key} className="flex items-center gap-2 text-xs">
                <span className="w-32 text-zinc-300 truncate" title={r.key}>{r.key}</span>
                <div className="flex-1 bg-zinc-800 rounded-full h-3 overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-amber-500 to-orange-500" style={{ width: `${(r.value / maxMix) * 100}%` }}/>
                </div>
                <span className="w-24 text-right font-mono text-amber-400 font-semibold">
                  {yAxis === 'orders' ? r.value : fmt(r.value)}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-zinc-500 text-xs text-center py-4">No data for {xAxis} × {yAxis}.</div>
        )}
      </div>
    </div>
  );
}

/** Picker with optional multi-select (compare mode). */
function PickerCard({ label, accent, icon: Icon, search, onSearch, items, picked, onPick, onRemove, testid, multi }) {
  return (
    <div className={`bg-zinc-900 border border-${accent}-500/30 rounded-xl p-4`} data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Icon size={14} className={`text-${accent}-400`}/>
          <span className="text-zinc-300 text-sm font-semibold">{label}{multi && <span className="text-zinc-500 text-[10px] ml-2">(up to 3)</span>}</span>
        </div>
      </div>

      {picked.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {picked.map(p => (
            <span key={p.id}
              data-testid={`${testid}-chip-${p.id}`}
              className={`inline-flex items-center gap-1.5 bg-${accent}-500/15 border border-${accent}-500/30 text-${accent}-300 text-xs px-2 py-1 rounded-full`}>
              {p.name}
              <button onClick={() => onRemove(p.id)} data-testid={`${testid}-chip-remove-${p.id}`}><X size={10}/></button>
            </span>
          ))}
        </div>
      )}

      {(multi || picked.length === 0) && (
        <>
          <div className="relative">
            <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-600"/>
            <input value={search} onChange={e => onSearch(e.target.value)}
              placeholder={`Search ${label.toLowerCase()}…`}
              data-testid={`${testid}-input`}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg pl-7 pr-2 py-2 text-zinc-100 text-sm focus:outline-none focus:border-amber-500"/>
          </div>
          <div className="mt-2 space-y-1 max-h-44 overflow-y-auto">
            {items.length === 0 ? (
              <div className="text-zinc-500 text-xs px-2 py-1">No matches.</div>
            ) : items.map(it => {
              const isPicked = picked.find(p => p.id === it.id);
              return (
                <button key={it.id} onClick={() => onPick(it)}
                  disabled={isPicked}
                  data-testid={`${testid}-option-${it.id}`}
                  className={`w-full text-left text-xs px-2 py-1.5 rounded ${isPicked ? 'text-zinc-600 cursor-default' : 'text-zinc-300 hover:bg-zinc-800 hover:text-zinc-100'}`}>
                  {it.name} {isPicked && <span className="text-[10px] text-zinc-600">(picked)</span>}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

/** Compare table: side-by-side metric rows across 2-3 entities. */
function CompareTable({ data, loading }) {
  const cols = data.columns;
  const isClient = data.entity_type === 'client';
  // Pick the right ordered metric keys.
  const keys = isClient
    ? [
        ['orders', 'Billed Orders', (v) => v],
        ['revenue', 'Revenue', fmt],
        ['paid', 'Paid', fmt],
        ['outstanding', 'Outstanding', fmt],
        ['avg_order_value', 'Avg Order Value', fmt],
        ['products_bought', 'Distinct Products', (v) => v],
        ['top_product', 'Top Product', (v) => v || '—'],
        ['top_product_revenue', '  ↳ revenue', fmt],
        ['last_order_date', 'Last Order', (v) => v || '—'],
        ['credit_period_days', 'Credit Period (days)', (v) => v ?? '—'],
      ]
    : [
        ['total_qty', 'Total Qty', fmtN],
        ['revenue', 'Revenue', fmt],
        ['avg_rate', 'Avg Selling Rate', fmt],
        ['min_rate', 'Min Rate', fmt],
        ['max_rate', 'Max Rate', fmt],
        ['clients_count', 'Distinct Clients', (v) => v],
        ['top_client', 'Top Client', (v) => v || '—'],
        ['top_client_revenue', '  ↳ revenue', fmt],
        ['gst_rate', 'GST %', (v) => v != null ? `${v}%` : '—'],
        ['unit', 'Unit', (v) => v || '—'],
      ];

  return (
    <div className="bg-zinc-900 border-2 border-orange-500/30 rounded-xl p-5 overflow-x-auto" data-testid="compare-panel">
      <div className="flex items-center gap-2 mb-3">
        <GitCompare size={14} className="text-orange-400"/>
        <span className="text-zinc-100 font-semibold text-sm">
          Side-by-side: {isClient ? 'Clients' : 'Products'}
        </span>
        {loading && <span className="text-zinc-500 text-[10px]">loading…</span>}
      </div>
      <table className="w-full text-xs" data-testid="compare-table">
        <thead>
          <tr className="border-b border-zinc-800">
            <th className="text-left px-3 py-2 text-zinc-500 uppercase tracking-wider text-[10px] w-44">Metric</th>
            {cols.map(c => (
              <th key={c.id} className="text-right px-3 py-2 text-orange-300 font-semibold" data-testid={`compare-col-${c.id}`}>
                {c.name}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {keys.map(([key, label, fmtFn]) => (
            <tr key={key} className="border-b border-zinc-800/40">
              <td className="px-3 py-1.5 text-zinc-400">{label}</td>
              {cols.map(c => {
                const v = c.metrics?.[key];
                const winner = key === 'revenue' || key === 'orders' || key === 'total_qty' || key === 'paid'
                  ? Math.max(...cols.map(x => Number(x.metrics?.[key]) || 0)) === (Number(v) || 0) && (Number(v) || 0) > 0
                  : false;
                const loser = key === 'outstanding'
                  ? Math.max(...cols.map(x => Number(x.metrics?.[key]) || 0)) === (Number(v) || 0) && (Number(v) || 0) > 0
                  : false;
                return (
                  <td key={c.id}
                    data-testid={`compare-cell-${c.id}-${key}`}
                    className={`px-3 py-1.5 text-right font-mono ${winner ? 'text-emerald-400 font-bold' : loser ? 'text-rose-400 font-bold' : 'text-zinc-200'}`}>
                    {v == null || v === '' ? '—' : fmtFn(v)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="text-[10px] text-zinc-600 mt-2">
        <span className="text-emerald-400">●</span> highest revenue/orders/qty/paid · <span className="text-rose-400">●</span> highest outstanding
      </div>
    </div>
  );
}

/** Per-client deep dive with KPI ribbon + month cross-tab (drill-down) + product mix + micro AI. */
function ClientDeepDive({ detail, microInsights, microLoading, onMicro, expandedMonth, monthInvoices, onToggleMonth }) {
  const kpis = [
    { label: 'Revenue', val: fmt(detail.total_revenue), tone: 'emerald', icon: DollarSign },
    { label: 'Outstanding', val: fmt(detail.outstanding), tone: 'rose', icon: Wallet },
    { label: 'Avg Order', val: fmt(detail.avg_order_value), tone: 'amber', icon: Receipt },
    { label: 'Cadence', val: detail.order_frequency_days != null ? `${detail.order_frequency_days}d` : '—', tone: 'sky', icon: Repeat },
    { label: 'Best Month', val: detail.best_month || '—', tone: 'fuchsia', icon: Calendar },
    { label: 'Top Product', val: detail.best_product || '—', tone: 'violet', icon: Award },
  ];
  return (
    <>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-zinc-100 text-lg font-bold" data-testid="dd-client-name">{detail.client.name}</div>
          <div className="text-zinc-500 text-xs mt-1">
            {detail.total_orders} billed orders{detail.returns?.count > 0 && <span className="text-teal-300"> · {detail.returns.count} credit note{detail.returns.count > 1 ? 's' : ''} (−{fmt(detail.returns.amount)})</span>} · payment {detail.payment_score}
            {detail.avg_payment_punctuality_days != null && ` (avg ${detail.avg_payment_punctuality_days}d ${detail.avg_payment_punctuality_days <= 0 ? 'early' : 'late'})`}
            {detail.client.phone && ` · ${detail.client.phone}`}
            {detail.client.email && ` · ${detail.client.email}`}
          </div>
        </div>
        <button onClick={onMicro} disabled={microLoading} data-testid="run-micro-insight"
          className="flex items-center gap-1.5 bg-fuchsia-500/15 hover:bg-fuchsia-500/25 text-fuchsia-300 border border-fuchsia-500/30 px-3 py-2 rounded-lg text-xs">
          <Sparkles size={12}/> {microLoading ? 'Analyzing…' : 'AI Micro Insight'}
        </button>
      </div>

      {/* 6 KPI ribbon */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
        {kpis.map(k => (
          <div key={k.label} className="bg-zinc-800/50 border border-zinc-800 rounded-lg p-2.5" data-testid={`dd-kpi-${k.label.toLowerCase().replace(' ','-')}`}>
            <div className="flex items-center gap-1.5">
              <k.icon size={11} className={`text-${k.tone}-400`}/>
              <span className="text-zinc-500 text-[10px] uppercase tracking-wider">{k.label}</span>
            </div>
            <div className={`mt-1 text-${k.tone}-300 font-bold text-sm truncate`} title={k.val}>{k.val}</div>
          </div>
        ))}
      </div>

      {microInsights.length > 0 && (
        <div className="bg-fuchsia-500/10 border border-fuchsia-500/30 rounded-lg p-3" data-testid="micro-insights">
          <ul className="space-y-1 text-zinc-200 text-xs">
            {microInsights.map((ins, i) => <li key={i} className="flex gap-2"><ArrowRight size={12} className="text-fuchsia-400 flex-shrink-0 mt-0.5"/><span>{ins}</span></li>)}
          </ul>
        </div>
      )}

      {/* Month × Product matrix with drill-down */}
      {detail.month_product_matrix && detail.month_product_matrix.length > 0 ? (
        <div className="border border-zinc-800 rounded-lg overflow-x-auto">
          <table className="w-full text-xs" data-testid="month-product-matrix">
            <thead className="bg-zinc-900">
              <tr className="text-zinc-500 text-[10px] uppercase tracking-wider">
                <th className="text-left px-3 py-2 w-32"></th>
                <th className="text-left px-3 py-2">Month / Product</th>
                <th className="text-right px-3 py-2 w-24">Quantity</th>
                <th className="text-right px-3 py-2 w-24">Avg Rate</th>
                <th className="text-right px-3 py-2 w-28">Revenue</th>
              </tr>
            </thead>
            <tbody>
              {detail.month_product_matrix.map(row => {
                const open = expandedMonth === row.month;
                const invoices = monthInvoices[row.month] || [];
                return (
                  <React.Fragment key={row.month}>
                    {/* Month header row — clickable for drill-down */}
                    <tr className="bg-zinc-800/30 border-t border-zinc-800/60">
                      <td colSpan={1} className="px-3 py-2">
                        <button onClick={() => onToggleMonth(row.month)}
                          data-testid={`mp-month-toggle-${row.month}`}
                          className="flex items-center gap-1.5 text-amber-400 font-mono font-bold hover:text-amber-300">
                          {open ? <ChevronDown size={14}/> : <ChevronRight size={14}/>}
                          {row.month}
                        </button>
                      </td>
                      <td className="px-3 py-2 text-zinc-500 text-[10px]">{row.orders} invoice{row.orders === 1 ? '' : 's'}</td>
                      <td className="px-3 py-2"></td>
                      <td className="px-3 py-2"></td>
                      <td className="px-3 py-2 text-right text-emerald-300 font-mono font-bold">{fmt(row.revenue)}</td>
                    </tr>
                    {/* Product rows for this month */}
                    {row.products.map(p => (
                      <tr key={`${row.month}-${p.product}`} className="border-t border-zinc-800/40" data-testid={`mp-row-${row.month}-${p.product}`}>
                        <td className="px-3 py-1.5"></td>
                        <td className="px-3 py-1.5 text-zinc-200 pl-6">└ {p.product}</td>
                        <td className="px-3 py-1.5 text-right text-zinc-300 font-mono">{fmtN(p.qty)} <span className="text-zinc-600">{p.unit}</span></td>
                        <td className="px-3 py-1.5 text-right text-zinc-500 font-mono">{fmt(p.avg_rate)}</td>
                        <td className="px-3 py-1.5 text-right text-emerald-400 font-mono font-semibold">{fmt(p.revenue)}</td>
                      </tr>
                    ))}
                    {/* Drill-down: individual invoices */}
                    {open && (
                      <tr className="bg-zinc-950">
                        <td colSpan={5} className="px-3 py-3" data-testid={`mp-drilldown-${row.month}`}>
                          {invoices.length === 0 ? (
                            <div className="text-zinc-500 text-xs italic">Loading invoices…</div>
                          ) : (
                            <div className="space-y-1.5">
                              <div className="text-zinc-500 text-[10px] uppercase tracking-wider">Invoices in {row.month}</div>
                              <table className="w-full text-[11px]">
                                <thead>
                                  <tr className="text-zinc-600">
                                    <th className="text-left py-1 px-2">Invoice #</th>
                                    <th className="text-left py-1 px-2">Date</th>
                                    <th className="text-left py-1 px-2">Items</th>
                                    <th className="text-right py-1 px-2">Total</th>
                                    <th className="text-right py-1 px-2">Paid</th>
                                    <th className="text-right py-1 px-2">Outstanding</th>
                                    <th className="text-left py-1 px-2">Status</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {invoices.map(inv => (
                                    <tr key={inv.id} className={`border-t border-zinc-800/40 ${inv.type === 'credit_note' ? 'bg-teal-950/30' : ''}`} data-testid={`drill-invoice-${inv.id}`}>
                                      <td className={`py-1 px-2 font-mono ${inv.type === 'credit_note' ? 'text-teal-300' : 'text-amber-400'}`}>{inv.invoice_number}{inv.type === 'credit_note' && <span className="ml-1 text-[9px] bg-teal-900/50 text-teal-200 px-1 rounded">RETURN</span>}</td>
                                      <td className="py-1 px-2 text-zinc-400 font-mono">{inv.invoice_date}</td>
                                      <td className="py-1 px-2 text-zinc-300">
                                        {inv.items.map(i => `${i.product} × ${fmtN(i.qty)}`).join(', ') || '—'}
                                      </td>
                                      <td className="py-1 px-2 text-right text-emerald-400 font-mono">{fmt(inv.total)}</td>
                                      <td className="py-1 px-2 text-right text-sky-400 font-mono">{fmt(inv.paid)}</td>
                                      <td className="py-1 px-2 text-right text-rose-400 font-mono">{fmt(inv.outstanding)}</td>
                                      <td className="py-1 px-2 text-zinc-500">{inv.payment_status || '—'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-zinc-500 text-xs">No billed orders yet for this client.</div>
      )}

      {/* iter159 — Per-product monthly bar charts */}
      {detail.per_product_monthly && detail.per_product_monthly.length > 0 && (
        <PerProductMonthly data={detail.per_product_monthly} />
      )}

      {/* iter159 — Order timing pattern (day-of-month + day-of-week) */}
      {detail.order_timing && (
        <OrderTimingPanel timing={detail.order_timing} totalOrders={detail.total_orders} />
      )}

      {/* iter159 — Predictive next-order forecast */}
      {detail.predictions && detail.predictions.length > 0 && (
        <PredictionsPanel predictions={detail.predictions} />
      )}

      {/* iter159 — Procurement forecast based on predictions */}
      {detail.procurement_forecast && detail.procurement_forecast.length > 0 && (
        <ProcurementPanel
          materials={detail.procurement_forecast}
          horizon={detail.procurement_horizon_days}
        />
      )}

      {/* Product mix mini bars */}
      {detail.product_mix.length > 0 && (
        <div>
          <div className="text-zinc-400 text-[11px] uppercase tracking-wider mb-2">Product Mix (top 5 by revenue)</div>
          <div className="space-y-1.5">
            {detail.product_mix.slice(0, 5).map(p => {
              const max = Math.max(1, detail.product_mix[0].revenue);
              return (
                <div key={p.product} className="flex items-center gap-2 text-xs">
                  <span className="w-40 text-zinc-300 truncate">{p.product}</span>
                  <div className="flex-1 bg-zinc-800 rounded-full h-2.5 overflow-hidden">
                    <div className="h-full bg-emerald-500" style={{ width: `${(p.revenue / max) * 100}%` }}/>
                  </div>
                  <span className="w-20 text-right font-mono text-emerald-400">{fmt(p.revenue)}</span>
                  <span className="w-16 text-right font-mono text-zinc-500 text-[10px]" title={p.returned_qty > 0 ? `net of ${fmtN(p.returned_qty)} ${p.unit} returned` : ''}>{fmtN(p.qty)} {p.unit}</span>
                  {p.returned_qty > 0 && <span className="text-[9px] font-mono text-teal-300 bg-teal-900/40 border border-teal-800/40 px-1 rounded" data-testid={`mix-returned-${p.product}`}>−{fmtN(p.returned_qty)} ret.</span>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Credit notes / sales returns — what came back from this customer */}
      {detail.returns?.count > 0 && (
        <div data-testid="client-returns-panel">
          <div className="text-zinc-400 text-[11px] uppercase tracking-wider mb-2">Returns · Credit Notes ({detail.returns.count} · {fmt(detail.returns.amount)})</div>
          <div className="space-y-1">
            {detail.returns.list.map(r => (
              <div key={r.voucher_number} className="flex items-center justify-between flex-wrap gap-2 bg-zinc-900 border border-teal-900/40 rounded-lg px-3 py-1.5 text-xs" data-testid={`client-return-${r.voucher_number}`}>
                <div className="min-w-0">
                  <span className="text-teal-300 font-mono">{r.voucher_number}</span>
                  <span className="text-zinc-500"> · {r.date}</span>
                  <span className="text-zinc-300"> · {r.items.map(i => `${i.product} × ${fmtN(i.qty)} ${i.unit}`).join(', ')}</span>
                  {r.narration && <span className="text-zinc-500 italic"> — {r.narration}</span>}
                </div>
                <div className="flex items-center gap-2 font-mono">
                  <span className={`text-[9px] px-1 rounded ${r.restocked ? 'bg-emerald-900/40 text-emerald-300' : 'bg-zinc-800 text-zinc-500'}`}>{r.restocked ? 'restocked' : 'no stock move'}</span>
                  <span className="text-teal-300">−{fmt(r.amount)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

/** Per-product deep dive — KPIs + price stats + client mix + price trend + cost trend. */
function ProductDeepDive({ detail, microInsights, microLoading, onMicro }) {
  const priceMax = Math.max(1, ...(detail.price_history || []).map(p => p.rate));
  const kpis = [
    { label: 'Revenue', val: fmt(detail.total_revenue), tone: 'emerald', icon: DollarSign },
    { label: 'Total Qty', val: `${fmtN(detail.total_qty)} ${detail.product.unit}`, tone: 'sky', icon: Package },
    { label: 'Avg Rate', val: fmt(detail.avg_price), tone: 'amber', icon: Receipt },
    { label: 'Rate Range', val: `${fmt(detail.min_price)}–${fmt(detail.max_price)}`, tone: 'violet', icon: Activity },
    { label: 'Avg Cost', val: fmt(detail.avg_cost), tone: 'rose', icon: Wallet },
    { label: 'Margin %', val: `${detail.margin_pct_est}%`, tone: 'fuchsia', icon: TrendingUp },
  ];
  return (
    <>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-zinc-100 text-lg font-bold">{detail.product.name}</div>
          <div className="text-zinc-500 text-xs mt-1">
            Sold across {detail.client_mix.length} clients
            {detail.product.gst_rate != null && ` · GST ${detail.product.gst_rate}%`}
            {detail.product.hsn_code && ` · HSN ${detail.product.hsn_code}`}
          </div>
        </div>
        <button onClick={onMicro} disabled={microLoading} data-testid="run-micro-insight"
          className="flex items-center gap-1.5 bg-fuchsia-500/15 hover:bg-fuchsia-500/25 text-fuchsia-300 border border-fuchsia-500/30 px-3 py-2 rounded-lg text-xs">
          <Sparkles size={12}/> {microLoading ? 'Analyzing…' : 'AI Micro Insight'}
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
        {kpis.map(k => (
          <div key={k.label} className="bg-zinc-800/50 border border-zinc-800 rounded-lg p-2.5" data-testid={`dd-prod-kpi-${k.label.toLowerCase().replace(/[^a-z]/g,'-')}`}>
            <div className="flex items-center gap-1.5">
              <k.icon size={11} className={`text-${k.tone}-400`}/>
              <span className="text-zinc-500 text-[10px] uppercase tracking-wider">{k.label}</span>
            </div>
            <div className={`mt-1 text-${k.tone}-300 font-bold text-sm truncate`} title={k.val}>{k.val}</div>
          </div>
        ))}
      </div>

      {microInsights.length > 0 && (
        <div className="bg-fuchsia-500/10 border border-fuchsia-500/30 rounded-lg p-3">
          <ul className="space-y-1 text-zinc-200 text-xs">
            {microInsights.map((ins, i) => <li key={i} className="flex gap-2"><ArrowRight size={12} className="text-fuchsia-400 flex-shrink-0 mt-0.5"/><span>{ins}</span></li>)}
          </ul>
        </div>
      )}

      {/* iter160 — Monthly Sales bar chart (across ALL clients) */}
      {detail.monthly_sales && detail.monthly_sales.length > 0 && (
        <ProductMonthlySales monthly={detail.monthly_sales} unit={detail.product.unit} />
      )}

      {detail.client_mix.length > 0 && (
        <div>
          <div className="text-zinc-400 text-[11px] uppercase tracking-wider mb-2">Client Mix</div>
          <div className="space-y-1.5">
            {detail.client_mix.slice(0, 8).map(c => {
              const max = Math.max(1, detail.client_mix[0].revenue);
              return (
                <div key={c.client} className="flex items-center gap-2 text-xs">
                  <span className="w-40 text-zinc-300 truncate">{c.client}</span>
                  <div className="flex-1 bg-zinc-800 rounded-full h-2.5 overflow-hidden">
                    <div className="h-full bg-sky-500" style={{ width: `${(c.revenue / max) * 100}%` }}/>
                  </div>
                  <span className="w-20 text-right font-mono text-sky-400">{fmt(c.revenue)}</span>
                  <span className="w-16 text-right font-mono text-zinc-500 text-[10px]">{fmtN(c.qty)} qty</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {detail.price_history && detail.price_history.length > 1 && (
        <div>
          <div className="text-zinc-400 text-[11px] uppercase tracking-wider mb-2">Price History (per invoice)</div>
          <div className="bg-zinc-800/50 rounded-lg p-2 h-32 relative">
            <svg width="100%" height="100%" viewBox={`0 0 ${detail.price_history.length * 12} 80`} preserveAspectRatio="none">
              {detail.price_history.map((p, i) => {
                const x = i * 12 + 4;
                const y = 80 - (p.rate / priceMax) * 70;
                return <circle key={i} cx={x} cy={y} r="2.5" fill="#f59e0b"><title>{`${p.date}: ${fmt(p.rate)} (${p.client})`}</title></circle>;
              })}
              {detail.price_history.length > 1 && (
                <polyline
                  fill="none" stroke="#f59e0b" strokeOpacity="0.4" strokeWidth="1"
                  points={detail.price_history.map((p, i) => `${i * 12 + 4},${80 - (p.rate / priceMax) * 70}`).join(' ')}
                />
              )}
            </svg>
          </div>
          <div className="flex justify-between text-[10px] text-zinc-600 mt-1">
            <span>{detail.price_history[0]?.date}</span>
            <span>min ₹{Math.min(...detail.price_history.map(p => p.rate))} / max ₹{Math.max(...detail.price_history.map(p => p.rate))}</span>
            <span>{detail.price_history[detail.price_history.length - 1]?.date}</span>
          </div>
        </div>
      )}
    </>
  );
}

/** Compact list used for Top Clients / Top Products on the macro view. */
function TopList({ icon: Icon, title, tone, testid, items, k1, k2, k2Label, k3, onItemClick }) {
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
      <div className="flex items-center gap-2 mb-3"><Icon size={14} className={`text-${tone}-400`}/><span className="text-zinc-300 text-sm font-semibold">{title}</span></div>
      <div className="space-y-2">
        {items.map((c, i) => (
          <button key={c[k1]} onClick={() => onItemClick(c)}
            data-testid={`${testid}-${i}`}
            className="w-full flex items-center justify-between py-1 border-b border-zinc-800/40 last:border-0 hover:bg-zinc-800/40 rounded px-1 transition-colors text-left">
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-zinc-500 text-[10px] w-4">{i + 1}</span>
              <span className="text-zinc-200 text-xs truncate">{c[k1]}</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-zinc-500 text-[10px]">{c[k2]} {k2Label}</span>
              <span className={`text-${tone}-400 text-xs font-mono font-semibold`}>{fmt(c[k3])}</span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}


/** iter159 — Per-product monthly bar series. Each product gets its own
 *  mini multi-bar (one per month) so operators see seasonality per item. */
function PerProductMonthly({ data }) {
  const [selectedIdx, setSelectedIdx] = useState(0);
  const product = data[selectedIdx] || data[0];
  if (!product) return null;
  const series = product.series;
  const maxQ = Math.max(1, ...series.map(s => s.qty));
  return (
    <div className="border border-zinc-800 rounded-lg p-4" data-testid="per-product-monthly">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Package size={14} className="text-sky-400"/>
          <span className="text-zinc-300 text-sm font-semibold">Per-product Monthly Trend</span>
        </div>
        <div className="flex items-center gap-1 flex-wrap">
          {data.slice(0, 8).map((p, i) => (
            <button key={p.product} onClick={() => setSelectedIdx(i)}
              data-testid={`ppm-product-tab-${i}`}
              className={`text-[11px] px-2 py-1 rounded-full border ${
                i === selectedIdx
                  ? 'bg-sky-500/20 text-sky-300 border-sky-500/40'
                  : 'bg-zinc-800/50 text-zinc-400 border-zinc-700 hover:text-zinc-200'
              }`}>
              {p.product}
            </button>
          ))}
        </div>
      </div>
      <div className="flex gap-3 h-36 bg-zinc-950 rounded-lg p-3 border border-zinc-800">
        {series.map(s => {
          const h = (s.qty / maxQ) * 100;
          return (
            <div key={s.month} className="flex-1 flex flex-col items-center gap-1 h-full" data-testid={`ppm-bar-${s.month}`}>
              <div className="text-[10px] font-mono text-sky-300 font-semibold">{s.qty ? fmtN(s.qty) : ''}</div>
              <div className="w-full bg-zinc-900 rounded-t overflow-hidden flex flex-col-reverse border border-zinc-800 flex-1 min-h-0">
                {s.qty > 0 && (
                  <div className="bg-gradient-to-t from-sky-500 to-cyan-300 shadow-[0_-2px_8px_rgba(56,189,248,0.4)]"
                    style={{ height: `${Math.max(h, 4)}%` }}
                    title={`${s.month}: ${s.qty} ${product.unit} · ${fmt(s.revenue)} · ${s.orders} orders`}/>
                )}
              </div>
              <div className="text-[10px] text-zinc-400 font-mono">{s.month.slice(5)}</div>
            </div>
          );
        })}
      </div>
      <table className="w-full text-xs mt-3" data-testid="ppm-table">
        <thead>
          <tr className="text-zinc-500 text-[10px] uppercase tracking-wider border-b border-zinc-800">
            <th className="text-left py-1.5 px-2">Month</th>
            <th className="text-right py-1.5 px-2">Quantity</th>
            <th className="text-right py-1.5 px-2">Revenue</th>
            <th className="text-right py-1.5 px-2">Orders</th>
          </tr>
        </thead>
        <tbody>
          {series.filter(s => s.qty > 0).map(s => (
            <tr key={s.month} className="border-b border-zinc-800/40">
              <td className="py-1 px-2 text-amber-400 font-mono">{s.month}</td>
              <td className="py-1 px-2 text-right text-zinc-200 font-mono">{fmtN(s.qty)} <span className="text-zinc-600">{product.unit}</span></td>
              <td className="py-1 px-2 text-right text-emerald-400 font-mono">{fmt(s.revenue)}</td>
              <td className="py-1 px-2 text-right text-sky-400 font-mono">{s.orders}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** iter159 — When in the month does the client order (early/mid/late) + day-of-week. */
function OrderTimingPanel({ timing, totalOrders }) {
  const dom = timing.by_day_of_month || [];
  const dow = timing.by_day_of_week || [];
  const maxDow = Math.max(1, ...dow.map(d => d.count));
  const tones = { early: 'emerald', mid: 'amber', late: 'rose' };
  return (
    <div className="border border-zinc-800 rounded-lg p-4" data-testid="order-timing-panel">
      <div className="flex items-center gap-2 mb-3">
        <Clock size={14} className="text-amber-400"/>
        <span className="text-zinc-300 text-sm font-semibold">Order Timing Pattern</span>
        <span className="text-zinc-600 text-[10px]">({totalOrders} orders analysed)</span>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div>
          <div className="text-zinc-500 text-[10px] uppercase tracking-wider mb-2">Day of Month</div>
          <div className="space-y-2">
            {dom.map(b => (
              <div key={b.bucket} className="flex items-center gap-2 text-xs" data-testid={`dom-bucket-${b.bucket}`}>
                <span className={`w-20 text-${tones[b.bucket]}-300 capitalize text-[11px]`}>
                  {b.bucket} ({b.bucket === 'early' ? '1–10' : b.bucket === 'mid' ? '11–20' : '21–31'})
                </span>
                <div className="flex-1 bg-zinc-800 rounded-full h-3 overflow-hidden">
                  <div className={`h-full bg-${tones[b.bucket]}-500`} style={{ width: `${b.percent}%` }}/>
                </div>
                <span className={`w-14 text-right font-mono text-${tones[b.bucket]}-400 text-xs`}>{b.percent}%</span>
                <span className="w-8 text-right font-mono text-zinc-500 text-[10px]">{b.count}</span>
              </div>
            ))}
          </div>
        </div>
        <div>
          <div className="text-zinc-500 text-[10px] uppercase tracking-wider mb-2">Day of Week</div>
          <div className="flex gap-1.5 h-24 bg-zinc-950 rounded p-2 border border-zinc-800">
            {dow.map(d => (
              <div key={d.dow} className="flex-1 flex flex-col items-center gap-1 h-full" data-testid={`dow-bar-${d.dow}`}>
                <div className="text-[9px] font-mono text-zinc-500">{d.count || ''}</div>
                <div className="w-full bg-zinc-800 rounded-t flex flex-col-reverse flex-1 min-h-0">
                  <div className="bg-gradient-to-t from-violet-500 to-fuchsia-400" style={{ height: `${(d.count / maxDow) * 100}%` }}/>
                </div>
                <div className="text-[10px] text-zinc-500">{d.dow}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
      {timing.per_product && timing.per_product.length > 0 && (
        <div className="mt-4">
          <div className="text-zinc-500 text-[10px] uppercase tracking-wider mb-2">Per-product Dominant Timing</div>
          <div className="flex flex-wrap gap-1.5">
            {timing.per_product.map(pp => (
              <span key={pp.product}
                data-testid={`pp-timing-${pp.product}`}
                className="text-[10px] px-2 py-1 rounded-full border bg-zinc-800/50 text-zinc-300 border-zinc-700">
                {pp.product}: <span className={`text-${tones[pp.dominant] || 'zinc'}-300 font-semibold`}>{pp.dominant || '—'}</span>
                <span className="text-zinc-600 ml-1">({pp.early}/{pp.mid}/{pp.late})</span>
              </span>
            ))}
          </div>
          <div className="text-[10px] text-zinc-600 mt-1">Counts shown as (early / mid / late)</div>
        </div>
      )}
    </div>
  );
}

/** iter159 — Predicted next-order forecast per product. */
function PredictionsPanel({ predictions }) {
  return (
    <div className="border border-fuchsia-500/30 bg-fuchsia-500/5 rounded-lg p-4" data-testid="predictions-panel">
      <div className="flex items-center gap-2 mb-3">
        <Brain size={14} className="text-fuchsia-400"/>
        <span className="text-zinc-200 text-sm font-semibold">Predicted Next Orders</span>
        <span className="text-zinc-600 text-[10px]">based on order cadence + avg quantity</span>
      </div>
      <table className="w-full text-xs" data-testid="predictions-table">
        <thead>
          <tr className="text-zinc-500 text-[10px] uppercase tracking-wider border-b border-zinc-800">
            <th className="text-left py-1.5 px-2">Product</th>
            <th className="text-left py-1.5 px-2">Last Order</th>
            <th className="text-right py-1.5 px-2">Avg Gap</th>
            <th className="text-left py-1.5 px-2">Predicted Next</th>
            <th className="text-right py-1.5 px-2">Predicted Qty</th>
            <th className="text-right py-1.5 px-2">Confidence</th>
          </tr>
        </thead>
        <tbody>
          {predictions.map(p => (
            <tr key={p.product} className="border-b border-zinc-800/40" data-testid={`prediction-${p.product}`}>
              <td className="py-1.5 px-2 text-zinc-200">
                {p.product}
                {p.overdue && (
                  <span className="ml-2 text-[9px] bg-rose-500/20 text-rose-300 px-1.5 py-0.5 rounded-full font-bold">OVERDUE</span>
                )}
              </td>
              <td className="py-1.5 px-2 text-zinc-400 font-mono">{p.last_order_date}</td>
              <td className="py-1.5 px-2 text-right text-zinc-300 font-mono">{p.avg_gap_days}d</td>
              <td className="py-1.5 px-2 font-mono">
                <span className={p.overdue ? 'text-rose-400 font-bold' : 'text-emerald-400'}>
                  {p.predicted_next_date}
                </span>
                <span className="text-zinc-600 ml-1 text-[10px]">
                  ({p.days_until_predicted > 0 ? `in ${p.days_until_predicted}d` : `${Math.abs(p.days_until_predicted)}d ago`})
                </span>
              </td>
              <td className="py-1.5 px-2 text-right text-amber-300 font-mono font-semibold">{fmtN(p.predicted_qty)} {p.unit}</td>
              <td className="py-1.5 px-2 text-right">
                <div className="flex items-center justify-end gap-1.5">
                  <div className="w-12 bg-zinc-800 rounded-full h-1.5 overflow-hidden">
                    <div className="h-full bg-fuchsia-400" style={{ width: `${p.confidence * 100}%` }}/>
                  </div>
                  <span className="text-fuchsia-300 font-mono text-[10px] w-8">{Math.round(p.confidence * 100)}%</span>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** iter159 — Procurement forecast rolling up predictions to raw material needs. */
function ProcurementPanel({ materials, horizon }) {
  const reorderCount = materials.filter(m => m.action === 'REORDER').length;
  return (
    <div className="border border-amber-500/30 bg-amber-500/5 rounded-lg p-4" data-testid="procurement-panel">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Truck size={14} className="text-amber-400"/>
          <span className="text-zinc-200 text-sm font-semibold">Procurement Forecast</span>
          <span className="text-zinc-600 text-[10px]">next {horizon} days · based on predicted demand × recipe BOM</span>
        </div>
        {reorderCount > 0 && (
          <span className="text-[10px] bg-rose-500/20 text-rose-300 px-2 py-0.5 rounded-full font-bold flex items-center gap-1">
            <AlertTriangle size={10}/> {reorderCount} material{reorderCount === 1 ? '' : 's'} short
          </span>
        )}
      </div>
      <table className="w-full text-xs" data-testid="procurement-table">
        <thead>
          <tr className="text-zinc-500 text-[10px] uppercase tracking-wider border-b border-zinc-800">
            <th className="text-left py-1.5 px-2">Raw Material</th>
            <th className="text-right py-1.5 px-2">Needed</th>
            <th className="text-right py-1.5 px-2">In Stock</th>
            <th className="text-right py-1.5 px-2">Shortage</th>
            <th className="text-right py-1.5 px-2">Est. Cost</th>
            <th className="text-left py-1.5 px-2">For Product(s)</th>
            <th className="text-center py-1.5 px-2">Status</th>
          </tr>
        </thead>
        <tbody>
          {materials.slice(0, 25).map(m => (
            <tr key={m.material} className="border-b border-zinc-800/40" data-testid={`procurement-${m.material}`}>
              <td className="py-1.5 px-2 text-zinc-200">{m.material}</td>
              <td className="py-1.5 px-2 text-right text-zinc-300 font-mono">{fmtN(m.needed)} {m.unit}</td>
              <td className="py-1.5 px-2 text-right text-sky-400 font-mono">{fmtN(m.current_stock)} {m.unit}</td>
              <td className="py-1.5 px-2 text-right font-mono">
                {m.shortage > 0 ? (
                  <span className="text-rose-400 font-semibold">{fmtN(m.shortage)} {m.unit}</span>
                ) : (
                  <span className="text-emerald-400">0</span>
                )}
              </td>
              <td className="py-1.5 px-2 text-right text-amber-300 font-mono">{m.estimated_cost > 0 ? fmt(m.estimated_cost) : '—'}</td>
              <td className="py-1.5 px-2 text-zinc-500 text-[10px] truncate max-w-[180px]" title={m.for_products?.join(', ')}>
                {m.for_products?.join(', ')}
              </td>
              <td className="py-1.5 px-2 text-center">
                {m.action === 'REORDER' ? (
                  <span className="text-[10px] bg-rose-500/20 text-rose-300 px-1.5 py-0.5 rounded-full font-bold">REORDER</span>
                ) : (
                  <span className="text-[10px] bg-emerald-500/20 text-emerald-300 px-1.5 py-0.5 rounded-full">OK</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {materials.length > 25 && (
        <div className="text-[10px] text-zinc-600 mt-2 text-center">+ {materials.length - 25} more materials</div>
      )}
    </div>
  );
}


/** iter161 — Client × Product focus banner.
 *
 *  Backed by the dedicated `/insights/client/{cid}/product/{pid}/monthly`
 *  endpoint which does case-insensitive substring matching, so it always
 *  finds the data even when the order item's product_name differs from
 *  the finished_goods name (e.g. "Cheese" picker → "Cheese 1KG" in items).
 */
function ClientProductFocus({ clientName, productName, data, loading }) {
  if (loading && !data) {
    return (
      <div className="border-2 border-orange-500/40 bg-orange-500/10 rounded-xl p-4 text-orange-200 text-sm"
        data-testid="client-product-focus">
        <div className="font-bold mb-1">{clientName} × {productName}</div>
        <div className="text-xs text-orange-300/80">Loading combined view…</div>
      </div>
    );
  }

  if (!data || data.total_orders === 0) {
    return (
      <div className="border-2 border-orange-500/40 bg-orange-500/10 rounded-xl p-4 text-orange-200 text-sm"
        data-testid="client-product-focus">
        <div className="font-bold mb-1">{clientName} × {productName}</div>
        <div className="text-xs text-orange-300/80">No purchases of this product by this client yet.</div>
      </div>
    );
  }

  const allSeries = data.series || [];
  const series = allSeries.filter(s => s.qty > 0);
  const maxQ = Math.max(1, ...allSeries.map(s => s.qty));
  const totalQty = data.total_qty;
  const totalRev = data.total_revenue;
  const totalOrders = data.total_orders;
  const avgRate = data.avg_rate;
  const monthsActive = series.length;
  const lastActive = series[series.length - 1];
  const firstActive = series[0];
  const variants = data.matched_variants || [];

  return (
    <div className="border-2 border-orange-500/40 bg-gradient-to-br from-orange-500/10 to-amber-500/5 rounded-xl p-5 space-y-4"
      data-testid="client-product-focus">
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <div className="text-orange-300 text-[10px] uppercase tracking-wider font-bold">Focused View</div>
          <div className="text-zinc-100 text-lg font-bold mt-1" data-testid="cpf-title">
            {clientName} <span className="text-zinc-500">×</span> {productName}
          </div>
          <div className="text-zinc-400 text-xs mt-1.5" data-testid="cpf-summary">
            <span className="text-orange-300 font-semibold">{clientName}</span>{' '}
            ordered <span className="text-emerald-300 font-bold">{fmtN(totalQty)} {data.product.unit}</span>{' '}
            of <span className="text-sky-300 font-semibold">{productName}</span>{' '}
            worth <span className="text-emerald-300 font-bold">{fmt(totalRev)}</span>{' '}
            across <span className="text-amber-300 font-bold">{totalOrders}</span>{' '}
            order{totalOrders === 1 ? '' : 's'} in <span className="text-amber-300 font-bold">{monthsActive}</span>{' '}
            month{monthsActive === 1 ? '' : 's'}.
            {data.avg_gap_days != null && (
              <span> Re-orders every <span className="text-fuchsia-300 font-bold">~{data.avg_gap_days}d</span>.</span>
            )}
          </div>
          {variants.length > 1 && (
            <div className="text-[10px] text-zinc-500 mt-1" data-testid="cpf-variants">
              Matched variants: <span className="text-zinc-400">{variants.join(' · ')}</span>
            </div>
          )}
        </div>
        <div className="grid grid-cols-2 gap-2 min-w-[260px]">
          <div className="bg-zinc-900/60 border border-zinc-800 rounded-lg p-2" data-testid="cpf-kpi-total-qty">
            <div className="text-[9px] text-zinc-500 uppercase tracking-wider">Total Qty</div>
            <div className="text-emerald-300 font-bold text-sm">{fmtN(totalQty)} {data.product.unit}</div>
          </div>
          <div className="bg-zinc-900/60 border border-zinc-800 rounded-lg p-2" data-testid="cpf-kpi-revenue">
            <div className="text-[9px] text-zinc-500 uppercase tracking-wider">Revenue</div>
            <div className="text-emerald-300 font-bold text-sm">{fmt(totalRev)}</div>
          </div>
          <div className="bg-zinc-900/60 border border-zinc-800 rounded-lg p-2" data-testid="cpf-kpi-avg-rate">
            <div className="text-[9px] text-zinc-500 uppercase tracking-wider">Avg Rate</div>
            <div className="text-amber-300 font-bold text-sm">{fmt(avgRate)}</div>
          </div>
          <div className="bg-zinc-900/60 border border-zinc-800 rounded-lg p-2" data-testid="cpf-kpi-peak">
            <div className="text-[9px] text-zinc-500 uppercase tracking-wider">Peak Month</div>
            <div className="text-fuchsia-300 font-bold text-sm">{data.peak_month || '—'}</div>
          </div>
        </div>
      </div>

      {/* Bar chart: this client × this product, month-by-month */}
      <div className="flex gap-3 h-32 bg-zinc-950/80 rounded-lg p-3 border border-zinc-800"
        data-testid="cpf-bar-chart">
        {allSeries.map(s => {
          const h = (s.qty / maxQ) * 100;
          return (
            <div key={s.month} className="flex-1 flex flex-col items-center gap-1 h-full" data-testid={`cpf-bar-${s.month}`}>
              <div className="text-[10px] font-mono text-orange-300 font-semibold">
                {s.qty ? fmtN(s.qty) : ''}
              </div>
              <div className="w-full bg-zinc-900 rounded-t overflow-hidden flex flex-col-reverse border border-zinc-800 flex-1">
                {s.qty > 0 && (
                  <div className="bg-gradient-to-t from-orange-500 to-amber-300 shadow-[0_-2px_8px_rgba(251,146,60,0.4)]"
                    style={{ height: `${Math.max(h, 4)}%` }}
                    title={`${s.month}: ${s.qty} ${data.product.unit} · ${fmt(s.revenue)} · ${s.orders} orders`}/>
                )}
              </div>
              <div className="text-[10px] text-zinc-400 font-mono">{s.month.slice(5)}</div>
            </div>
          );
        })}
      </div>

      {/* Month-level table */}
      <table className="w-full text-xs" data-testid="cpf-table">
        <thead>
          <tr className="text-zinc-500 text-[10px] uppercase tracking-wider border-b border-zinc-800">
            <th className="text-left py-1.5 px-2">Month</th>
            <th className="text-right py-1.5 px-2">Quantity</th>
            <th className="text-right py-1.5 px-2">Revenue</th>
            <th className="text-right py-1.5 px-2">Orders</th>
            <th className="text-left py-1.5 px-2">Invoices</th>
          </tr>
        </thead>
        <tbody>
          {series.map(s => (
            <tr key={s.month} className="border-b border-zinc-800/40">
              <td className="py-1 px-2 text-amber-400 font-mono">{s.month}</td>
              <td className="py-1 px-2 text-right text-zinc-200 font-mono">{fmtN(s.qty)} <span className="text-zinc-600">{data.product.unit}</span></td>
              <td className="py-1 px-2 text-right text-emerald-400 font-mono">{fmt(s.revenue)}</td>
              <td className="py-1 px-2 text-right text-sky-400 font-mono">{s.orders}</td>
              <td className="py-1 px-2 text-zinc-500 text-[10px] truncate max-w-[260px]" title={(s.invoice_numbers || []).join(', ')}>
                {(s.invoice_numbers || []).join(', ') || '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Inline narrative */}
      <div className="text-[11px] text-zinc-400 leading-relaxed" data-testid="cpf-narrative">
        First bought in <span className="text-amber-300 font-mono">{firstActive?.month || data.first_order_date?.slice(0,7) || '—'}</span>.
        {' '}Peak in <span className="text-fuchsia-300 font-mono">{data.peak_month || '—'}</span>{' '}
        ({fmtN(data.peak_qty)} {data.product.unit}).{' '}
        Most recent in <span className="text-emerald-300 font-mono">{lastActive?.month || '—'}</span>.
      </div>
    </div>
  );
}

/** iter160 — Product-only monthly sales bar chart (totals across all clients).
 *
 *  When the operator picks ONLY a product, this shows how much of it has
 *  been sold each month aggregated across every client — and a table with
 *  the exact numbers below the chart.
 */
function ProductMonthlySales({ monthly, unit }) {
  const maxQ = Math.max(1, ...monthly.map(m => m.qty));
  const totalQty = monthly.reduce((a, b) => a + (b.qty || 0), 0);
  const totalRev = monthly.reduce((a, b) => a + (b.revenue || 0), 0);
  const peak = monthly.reduce((a, b) => (b.qty > a.qty ? b : a), { qty: 0, month: '' });
  return (
    <div className="border border-zinc-800 rounded-lg p-4" data-testid="product-monthly-sales">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div className="flex items-center gap-2">
          <Activity size={14} className="text-cyan-400"/>
          <span className="text-zinc-300 text-sm font-semibold">Monthly Sales (all clients combined)</span>
        </div>
        <div className="text-[11px] text-zinc-400" data-testid="pms-summary">
          Total <span className="text-emerald-300 font-bold">{fmtN(totalQty)} {unit}</span>{' '}
          · <span className="text-emerald-300 font-bold">{fmt(totalRev)}</span>{' '}
          · peak in <span className="text-fuchsia-300 font-mono">{peak.month || '—'}</span>
        </div>
      </div>
      <div className="flex gap-3 h-36 bg-zinc-950 rounded-lg p-3 border border-zinc-800"
        data-testid="pms-bar-chart">
        {monthly.map(m => {
          const h = (m.qty / maxQ) * 100;
          return (
            <div key={m.month} className="flex-1 flex flex-col items-center gap-1 h-full" data-testid={`pms-bar-${m.month}`}>
              <div className="text-[10px] font-mono text-cyan-300 font-semibold">{m.qty ? fmtN(m.qty) : ''}</div>
              <div className="w-full bg-zinc-900 rounded-t overflow-hidden flex flex-col-reverse border border-zinc-800 flex-1 min-h-0">
                {m.qty > 0 && (
                  <div className="bg-gradient-to-t from-cyan-500 to-emerald-300 shadow-[0_-2px_8px_rgba(34,211,238,0.4)]"
                    style={{ height: `${Math.max(h, 4)}%` }}
                    title={`${m.month}: ${m.qty} ${unit} · ${fmt(m.revenue)}`}/>
                )}
              </div>
              <div className="text-[10px] text-zinc-400 font-mono">{m.month.slice(5)}</div>
            </div>
          );
        })}
      </div>
      <table className="w-full text-xs mt-3" data-testid="pms-table">
        <thead>
          <tr className="text-zinc-500 text-[10px] uppercase tracking-wider border-b border-zinc-800">
            <th className="text-left py-1.5 px-2">Month</th>
            <th className="text-right py-1.5 px-2">Quantity</th>
            <th className="text-right py-1.5 px-2">Revenue</th>
            <th className="text-right py-1.5 px-2">% of total</th>
          </tr>
        </thead>
        <tbody>
          {monthly.map(m => (
            <tr key={m.month} className="border-b border-zinc-800/40">
              <td className="py-1 px-2 text-amber-400 font-mono">{m.month}</td>
              <td className="py-1 px-2 text-right text-zinc-200 font-mono">{fmtN(m.qty)} <span className="text-zinc-600">{unit}</span></td>
              <td className="py-1 px-2 text-right text-emerald-400 font-mono">{fmt(m.revenue)}</td>
              <td className="py-1 px-2 text-right text-zinc-500 font-mono">
                {totalRev ? `${((m.revenue / totalRev) * 100).toFixed(1)}%` : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


// ── Raw-material lens — same depth as the client/product deep dive ──────────
function RawMaterialDeepDive({ detail, canSeePrices = true }) {
  const d = detail;
  const runout = d.days_to_runout;
  const runoutCls = runout == null
    ? 'bg-zinc-500/10 border-zinc-500/40 text-zinc-300'
    : runout <= 5 ? 'bg-red-500/10 border-red-500/40 text-red-300'
    : runout <= 14 ? 'bg-amber-500/10 border-amber-500/40 text-amber-300'
    : 'bg-emerald-500/10 border-emerald-500/40 text-emerald-300';
  const trendUp = (d.price_trend_pct || 0) > 0;
  const kpis = [
    { label: 'On hand', value: `${fmtN(d.stock)} ${d.unit}` },
    { label: 'Net available', value: `${fmtN(d.net_available)} ${d.unit}`, hint: d.reserved ? `${fmtN(d.reserved)} reserved` : '' },
    ...(canSeePrices ? [{ label: 'Stock value', value: fmt(d.value) }] : []),
    { label: 'Daily use (90d)', value: `${fmtN(d.daily_use)} ${d.unit}` },
    { label: 'Lead time', value: `${d.lead_days} d` },
    { label: 'Incoming', value: `${fmtN(d.incoming_qty)} ${d.unit}`, hint: d.incoming_eta !== '—' ? `ETA ${d.incoming_eta}` : '' },
  ];
  const histRates = (d.price_history || []).map(p => p.rate);
  const minR = Math.min(...(histRates.length ? histRates : [0]));
  const maxR = Math.max(...(histRates.length ? histRates : [1]));
  const maxMonth = Math.max(1, ...(d.monthly_consumption || []).map(m => m.qty));

  return (
    <div className="space-y-5" data-testid="rm-deep-dive">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Package size={18} className="text-amber-400" />
            <span className="text-zinc-100 text-lg font-bold" data-testid="dd-rm-name">{d.name}</span>
            {d.category && <span className="text-[10px] uppercase tracking-wide text-zinc-500 bg-zinc-800 px-2 py-0.5 rounded">{d.category}</span>}
          </div>
          {canSeePrices && <p className="text-zinc-500 text-xs mt-1">Last rate {fmt(d.last_rate)}/{d.unit} · avg {fmt(d.avg_rate)}/{d.unit}</p>}
        </div>
        <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs border ${runoutCls}`} data-testid="dd-rm-runout">
          <Clock size={13} />
          {runout == null ? 'No recent usage' : runout <= 5 ? `Runs out in ~${runout} d — order now` : `~${runout} days of cover`}
        </div>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
        {kpis.map(k => (
          <div key={k.label} className="bg-zinc-800/50 border border-zinc-800 rounded-lg p-2.5" data-testid={`dd-rm-kpi-${k.label.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
            <div className="text-[10px] text-zinc-500 uppercase tracking-wide">{k.label}</div>
            <div className="text-zinc-100 text-sm font-bold mt-0.5">{k.value}</div>
            {k.hint && <div className="text-[10px] text-zinc-500 mt-0.5">{k.hint}</div>}
          </div>
        ))}
      </div>

      {/* Price trend + history */}
      {canSeePrices && (
      <div className="bg-zinc-800/30 border border-zinc-800 rounded-lg p-4" data-testid="dd-rm-price">
        <div className="flex items-center justify-between mb-3">
          <span className="text-zinc-300 text-xs font-semibold flex items-center gap-1.5">
            {trendUp ? <TrendingUp size={13} className="text-red-400" /> : <TrendingDown size={13} className="text-emerald-400" />}
            Purchase price trend
          </span>
          <span className={`text-xs font-bold ${trendUp ? 'text-red-400' : 'text-emerald-400'}`}>
            {trendUp ? '+' : ''}{d.price_trend_pct}% recent
          </span>
        </div>
        {histRates.length ? (
          <div className="flex items-end gap-1 h-20" data-testid="dd-rm-price-chart">
            {d.price_history.slice(-24).map((p, i) => {
              const h = maxR === minR ? 60 : 10 + ((p.rate - minR) / (maxR - minR)) * 90;
              return (
                <div key={i} className="flex-1 group relative" title={`${p.date} · ${fmt(p.rate)} · ${p.vendor}`}>
                  <div className="bg-amber-500/50 group-hover:bg-amber-400 rounded-t transition-colors" style={{ height: `${h}%` }} />
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-zinc-500 text-xs">No priced purchase lots yet.</p>
        )}
      </div>
      )}

      {/* Vendors + monthly consumption */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-zinc-800/30 border border-zinc-800 rounded-lg p-4" data-testid="dd-rm-vendors">
          <div className="text-zinc-300 text-xs font-semibold mb-3 flex items-center gap-1.5"><Truck size={13} /> Vendors</div>
          {(d.vendors || []).length ? (
            <div className="space-y-1.5">
              {d.vendors.slice(0, 6).map(v => (
                <div key={v.name} className="flex items-center justify-between text-xs">
                  <span className="text-zinc-300 truncate mr-2">{v.name}</span>
                  <span className="text-zinc-500 whitespace-nowrap">{fmtN(v.qty)} {d.unit}{canSeePrices ? ` · ${fmt(v.last_rate)}` : ''}</span>
                </div>
              ))}
            </div>
          ) : <p className="text-zinc-500 text-xs">No vendor history.</p>}
        </div>
        <div className="bg-zinc-800/30 border border-zinc-800 rounded-lg p-4" data-testid="dd-rm-consumption">
          <div className="text-zinc-300 text-xs font-semibold mb-3 flex items-center gap-1.5"><Activity size={13} /> Monthly consumption</div>
          {(d.monthly_consumption || []).length ? (
            <div className="flex items-end gap-1.5 h-20">
              {d.monthly_consumption.slice(-12).map(m => (
                <div key={m.month} className="flex-1 group relative" title={`${m.month} · ${fmtN(m.qty)} ${d.unit}`}>
                  <div className="bg-sky-500/50 group-hover:bg-sky-400 rounded-t transition-colors" style={{ height: `${10 + (m.qty / maxMonth) * 90}%` }} />
                  <div className="text-[8px] text-zinc-600 text-center mt-1 truncate">{m.month.slice(5)}</div>
                </div>
              ))}
            </div>
          ) : <p className="text-zinc-500 text-xs">No consumption recorded in the last 90 days.</p>}
        </div>
      </div>

      {/* Recent lots */}
      <div className="overflow-x-auto" data-testid="dd-rm-lots">
        <div className="text-zinc-300 text-xs font-semibold mb-2">Recent lots</div>
        <table className="w-full text-xs">
          <thead>
            <tr className="text-zinc-500 border-b border-zinc-800">
              <th className="text-left px-2 py-1.5">Lot</th>
              <th className="text-left px-2 py-1.5">Date</th>
              <th className="text-left px-2 py-1.5">Vendor</th>
              {canSeePrices && <th className="text-right px-2 py-1.5">Rate</th>}
              <th className="text-right px-2 py-1.5">Received</th>
              <th className="text-right px-2 py-1.5">Remaining</th>
            </tr>
          </thead>
          <tbody>
            {(d.recent_lots || []).map((l, i) => (
              <tr key={i} className="border-b border-zinc-800/40">
                <td className="px-2 py-1.5 text-zinc-400 font-mono">{l.lot_no || '—'}</td>
                <td className="px-2 py-1.5 text-zinc-400">{l.date || '—'}</td>
                <td className="px-2 py-1.5 text-zinc-300 truncate max-w-[140px]">{l.vendor || '—'}</td>
                {canSeePrices && <td className="px-2 py-1.5 text-right text-zinc-300">{fmt(l.rate)}</td>}
                <td className="px-2 py-1.5 text-right text-zinc-300">{fmtN(l.received)}</td>
                <td className="px-2 py-1.5 text-right text-zinc-400">{fmtN(l.remaining)}</td>
              </tr>
            ))}
            {!(d.recent_lots || []).length && (
              <tr><td colSpan={canSeePrices ? 6 : 5} className="px-2 py-3 text-zinc-500 text-center">No lots recorded.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

